import React, { forwardRef, ReactElement, ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import { getDownloadURL, ref as firebaseStorageRef } from 'firebase/storage';

import { Link } from 'react-router';

import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardActions from '@mui/material/CardActions';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import { styled, useTheme, useThemeProps } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import { useApp } from '../../contextProviders/AppProvider';
import { storage } from '../../firebase';
import { APPBAR_HEIGHT_REM, APPBAR_STICKY_TOP } from '../../styles/mui6Theme';

import GratiMap from './GratiMap';

export interface GratiCardDetail {
  icon?: ReactElement;
  preText?: string;
  text: string;
  postText?: string;
  status?: string;
  badge?: ReactElement;
  action?: string;
  actionIcon?: ReactElement;
  actionHandler?: () => void;
  highlight?: boolean;
}

export interface GratiCardAction {
  icon?: ReactElement;
  text: string;
  actionHandler: () => void;
  variant?: 'text' | 'contained' | 'outlined';
}

/** Optional click handlers for making card slots interactive (e.g., for preview editing) */
export interface GratiCardSlotClickHandlers {
  colorbar?: () => void;
  avatar?: () => void;
  title?: () => void;
  subtitle?: () => void;
  category?: () => void;
  spotlight?: () => void;
  state?: () => void;
  description?: () => void;
  detail?: (index: number) => void;
}

export interface GratiCardProps {
  key: string;
  to?: string; // Card header will link this url
  onNavigate?: () => void; // When provided, intercepts primary-button taps (left-click, no modifiers) instead of following `to` — preserves right-click / middle-click / Cmd+click behaviour
  // Props are in the order they are rendered.
  collapsible?: boolean; // When true, card body/details/actions can be collapsed
  defaultExpanded?: boolean; // Initial expanded state when collapsible (default: true)
  stickyOnScroll?: boolean; // When true, card sticks to top and auto-collapses on scroll
  expandLabel?: string; // Label for the expand affordance button shown when collapsed
  collapsedSummary?: ReactNode; // When provided, shown instead of the default title-only row in collapsed state
  titleInHeader?: boolean; // When true, title (and subtitle) render inside the header context column
  onExpandedChange?: (expanded: boolean) => void; // Callback when expanded state changes
  colorbar?: string;
  avatar?: string;
  avatarStorageLocation?: string;
  category?: string | string[];
  state?: string | GratiCardAction;
  spotlight?: ReactElement;
  spotlightCaption?: ReactNode; // Small grey caption rendered below (outside) the spotlight box
  topActions?: GratiCardAction | GratiCardAction[]; // Action(s) to show in the top-right corner of the card
  headerActions?: ReactElement; // Additional icon buttons rendered to the left of topActions in the header
  alert?: string;
  title?: string;
  subtitle?: string | ReactNode;
  placeId?: string;
  description?: string;
  details?: GratiCardDetail | GratiCardDetail[];
  actionbar?: ReactElement;
  variant: 'elevation' | 'outlined' | 'test' | 'none';
  /** Optional click handlers for making card slots interactive */
  slotClickHandlers?: GratiCardSlotClickHandlers;
  sx?: SxProps<Theme>;
}

interface GratiCardOwnerState extends GratiCardProps {}

const GratiCardRoot = styled(Card, {
  name: 'GratiCard',
  slot: 'root',
  overridesResolver: (props, styles) => {
    return styles.root;
  },
})<{ ownerState: GratiCardOwnerState }>(() => ({
  display: 'flex',
  flexDirection: 'column',
}));

const GratiCardColorbar = styled('div', {
  name: 'GratiCard',
  slot: 'colorbar',
  overridesResolver: (props, styles) => {
    return styles.colorbar;
  },
})<{ ownerState: GratiCardOwnerState }>(() => ({}));


const GratiCardHeader = styled('div', {
  name: 'GratiCard',
  slot: 'header',
  overridesResolver: (props, styles) => {
    return styles.header;
  },
})<{ ownerState: GratiCardOwnerState }>(() => ({}));

const GratiCardHeaderContent = styled('div', {
  name: 'GratiCard',
  slot: 'headercontent',
  overridesResolver: (props, styles) => {
    return styles.headercontent;
  },
})<{ ownerState: GratiCardOwnerState }>(() => ({}));

const GratiCardAvatar = styled(Avatar, {
  name: 'GratiCard',
  slot: 'avatar',
  overridesResolver: (props, styles) => {
    return styles.avatar;
  },
})<{ ownerState: GratiCardOwnerState }>(() => ({}));

const GratiCardContext = styled('div', {
  name: 'GratiCard',
  slot: 'context',
  overridesResolver: (props, styles) => {
    return styles.context;
  },
})<{ ownerState: GratiCardOwnerState }>(() => ({}));

const GratiCardCategory = styled('div', {
  name: 'GratiCard',
  slot: 'category',
  overridesResolver: (props, styles) => {
    return styles.category;
  },
})<{ ownerState: GratiCardOwnerState }>(() => ({}));

const GratiCardState = styled('div', {
  name: 'GratiCard',
  slot: 'state',
  overridesResolver: (props, styles) => {
    return styles.state;
  },
})<{ ownerState: GratiCardOwnerState }>(() => ({}));

const GratiCardSpotlight = styled('div', {
  name: 'GratiCard',
  slot: 'spotlight',
  overridesResolver: (props, styles) => {
    return styles.spotlight;
  },
})<{ ownerState: GratiCardOwnerState }>(() => ({}));

const GratiCardTopAction = styled('div', {
  name: 'GratiCard',
  slot: 'topaction',
  overridesResolver: (props, styles) => {
    return styles.topaction;
  },
})<{ ownerState: GratiCardOwnerState }>(() => ({}));

const GratiCardAlert = styled('span', {
  name: 'GratiCard',
  slot: 'alert',
  overridesResolver: (props, styles) => {
    return styles.alert;
  },
})<{ ownerState: GratiCardOwnerState }>(({ theme }) => ({
  ...theme.typography.title,
}));

const GratiCardBody = styled('div', {
  name: 'GratiCard',
  slot: 'body',
  overridesResolver: (props, styles) => {
    return styles.body;
  },
})<{ ownerState: GratiCardOwnerState }>(() => ({}));

const GratiCardTitle = styled('span', {
  name: 'GratiCard',
  slot: 'title',
  overridesResolver: (props, styles) => {
    return styles.title;
  },
})<{ ownerState: GratiCardOwnerState }>(({ theme }) => ({
  ...theme.typography.title,
}));

const GratiCardSubtitle = styled('span', {
  name: 'GratiCard',
  slot: 'subtitle',
  overridesResolver: (props, styles) => {
    return styles.subtitle;
  },
})<{ ownerState: GratiCardOwnerState }>(() => ({}));

const GratiCardDescription = styled('div', {
  name: 'GratiCard',
  slot: 'description',
  overridesResolver: (props, styles) => {
    return styles.description;
  },
})<{ ownerState: GratiCardOwnerState }>(() => ({}));

const GratiCardDetails = styled('div', {
  name: 'GratiCard',
  slot: 'details',
  overridesResolver: (props, styles) => {
    return styles.details;
  },
})<{ ownerState: GratiCardOwnerState }>(() => ({}));

const GratiCardDetailSlot = styled('div', {
  name: 'GratiCard',
  slot: 'detail',
  overridesResolver: (props, styles) => {
    return styles.detail;
  },
})<{ ownerState: GratiCardOwnerState }>(() => ({}));

const GratiCardActionbar = styled('div', {
  name: 'GratiCard',
  slot: 'actionbar',
  overridesResolver: (props, styles) => {
    return styles.actionbar;
  },
})<{ ownerState: GratiCardOwnerState }>(() => ({}));

const GratiCardCollapsedName = styled('div', {
  name: 'GratiCard',
  slot: 'collapsedname',
  overridesResolver: (props, styles) => {
    return styles.collapsedname;
  },
})<{ ownerState: GratiCardOwnerState }>(() => ({}));

const GratiCardExpandAffordance = styled('div', {
  name: 'GratiCard',
  slot: 'expandaffordance',
  overridesResolver: (props, styles) => {
    return styles.expandaffordance;
  },
})<{ ownerState: GratiCardOwnerState }>(() => ({}));

export default forwardRef<HTMLDivElement, GratiCardProps>(function GratiCard(inProps, ref) {
  const props = useThemeProps({ props: inProps, name: 'GratiCard' });

  const {
    to,
    onNavigate,
    collapsible = false,
    defaultExpanded = true,
    stickyOnScroll = false,
    expandLabel,
    collapsedSummary,
    titleInHeader = false,
    onExpandedChange,
    colorbar,
    avatar,
    avatarStorageLocation,
    category,
    state,
    spotlight,
    spotlightCaption,
    alert,
    title,
    subtitle,
    placeId,
    description,
    details,
    actionbar,
    topActions,
    headerActions,
    variant,
    slotClickHandlers,
    ...other
  } = props;

  // Styles for interactive slots
  const interactiveSlotSx = {
    cursor: 'pointer',
    borderRadius: 1,
    '&:hover': { backgroundColor: 'action.hover' },
  };

  const { updateActions } = useApp();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
  // The sticky + collapse-on-scroll treatment is for large screens only. On small screens the
  // card scrolls away with the page like any other content (see isScrolledPast below).
  const isStickyCollapse = stickyOnScroll && !isSmallScreen;
  const ownerState = { ...props, variant: variant ?? 'outlined' };
  const [logo, setLogo] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [appBarMenuOpen, setAppBarMenuOpen] = useState(false);
  const [expanded, setExpanded] = useState(defaultExpanded);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const internalRef = useRef<HTMLDivElement>(null);
  const collapsedHeaderRef = useRef<HTMLDivElement | null>(null);
  const storageRef = useMemo(
    () => avatarStorageLocation ? firebaseStorageRef(storage, avatarStorageLocation) : null,
    [avatarStorageLocation]
  );

  // stickyOnScroll collapse/expand logic (large screens).
  //
  // Small screens don't collapse: a collapse there has nothing to shrink to (the title is already
  // in the AppBar), so it reads as the card vanishing and everything below it leaping up by the
  // card's height. Instead the card stays in normal flow and scrolls away smoothly; only the top
  // actions move into the AppBar once the card's top edge passes under it (isScrolledPast).
  //
  // Root cause of feedback loops: browser scroll anchoring adjusts scrollY when the card's
  // document-flow height changes, creating oscillations:
  //   - Collapse loop: collapse → anchoring drops scrollY to 0 → re-expand fires.
  //   - Expand loop: expand → anchoring raises scrollY to ~200 → re-collapse fires.
  //
  // Fix: disable scroll anchoring (overflow-anchor: none) BEFORE triggering any height change.
  // Since the style is applied synchronously before React's state update flushes to the DOM,
  // anchoring is already off when the browser performs layout — so scrollY never jumps.
  //
  // Additionally, re-expand only fires when scrollY crosses from *above* 10 back to ≤ 10
  // (prev > 10 guard), or when scrollY goes negative (mobile overscroll/pull-down gesture).
  //
  // Short-page / desktop edge case: when collapsing makes the page shorter than the viewport,
  // the browser clamps scrollY to 0. The scroll event still fires but prev never exceeds 10
  // again, so the normal re-expand trigger never fires. A separate wheel event listener handles
  // this: if the user scrolls their wheel "upward" while already at scrollY=0 with the card
  // collapsed, that intent signal re-expands the card — without touching the scroll path.
  //
  // isCollapsingRef guards the 300ms collapse animation window to prevent duplicate collapses
  // (scrollY stays > 10 after collapse since anchoring is off, so without this guard the handler
  // would re-trigger on every subsequent scroll event).
  const isCollapsingRef = useRef(false);
  const collapseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const expandTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const expandedRef = useRef(expanded);
  const prevScrollYRef = useRef(typeof window !== 'undefined' ? window.scrollY : 0);
  useEffect(() => { expandedRef.current = expanded; }, [expanded]);

  useEffect(() => {
    if (!isStickyCollapse) return;

    const doExpand = () => {
      if (collapseTimerRef.current) { clearTimeout(collapseTimerRef.current); collapseTimerRef.current = null; }
      document.documentElement.style.overflowAnchor = 'none';
      setExpanded(defaultExpanded);
      if (expandTimerRef.current) clearTimeout(expandTimerRef.current);
      expandTimerRef.current = setTimeout(() => {
        document.documentElement.style.overflowAnchor = '';
      }, 300);
    };

    const handleScroll = () => {
      const prev = prevScrollYRef.current;
      const curr = window.scrollY;
      prevScrollYRef.current = curr;

      if (curr <= 10) {
        // Re-expand when scrollY crossed back from above 10 (scroll-back-to-top),
        // or went negative (mobile overscroll / pull-down gesture).
        if (!isCollapsingRef.current && (prev > 10 || curr < 0) && !expandedRef.current) {
          doExpand();
        }
        return;
      }

      // scrollY > 10 from here.
      if (isCollapsingRef.current) return;
      if (!expandedRef.current) return; // already collapsed
      // Disable anchoring first so scrollY doesn't drop back to 0 and immediately re-expand.
      isCollapsingRef.current = true;
      document.documentElement.style.overflowAnchor = 'none';
      setExpanded(false);
      if (collapseTimerRef.current) clearTimeout(collapseTimerRef.current);
      collapseTimerRef.current = setTimeout(() => {
        isCollapsingRef.current = false;
        document.documentElement.style.overflowAnchor = '';
      }, 300);
    };

    // Desktop short-page case: when the page becomes too short to scroll after collapse,
    // scrollY is clamped to 0 and no scroll event fires on "scroll up" intent. The wheel
    // event fires even with nothing to scroll — use it to detect upward intent at the top.
    const handleWheel = (e: WheelEvent) => {
      if (e.deltaY < 0 && window.scrollY === 0 && !expandedRef.current && !isCollapsingRef.current) {
        doExpand();
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('wheel', handleWheel, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('wheel', handleWheel);
      if (collapseTimerRef.current) clearTimeout(collapseTimerRef.current);
      if (expandTimerRef.current) clearTimeout(expandTimerRef.current);
      document.documentElement.style.overflowAnchor = '';
    };
  }, [isStickyCollapse, defaultExpanded]);

  // Small screens never collapse on scroll, so a card that collapsed at a large size must go
  // back to its default when the viewport shrinks, or it would be stuck with no way to expand.
  useEffect(() => {
    if (stickyOnScroll && isSmallScreen) setExpanded(defaultExpanded);
  }, [stickyOnScroll, isSmallScreen, defaultExpanded]);

  // Small screens: watch the card's top edge pass under the AppBar. rootBounds is the viewport
  // minus the AppBar; the bottom margin ignores the card's bottom edge so a card taller than the
  // viewport still reports "fully visible" once its top is back below the AppBar.
  const [isScrolledPast, setIsScrolledPast] = useState(false);
  useEffect(() => {
    if (!stickyOnScroll || !isSmallScreen) { setIsScrolledPast(false); return; }
    const el = internalRef.current;
    if (!el) return;
    const remPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    const appBarPx = isSmUp ? APPBAR_HEIGHT_REM.sm * remPx + 1 : APPBAR_HEIGHT_REM.xs * remPx;
    const observer = new IntersectionObserver(([entry]) => {
      setIsScrolledPast(!!entry.rootBounds && entry.boundingClientRect.top < entry.rootBounds.top);
    }, { rootMargin: `-${appBarPx}px 0px 100% 0px`, threshold: [0, 0.99, 1] });
    observer.observe(el);
    return () => observer.disconnect();
  }, [stickyOnScroll, isSmallScreen, isSmUp]);

  // On small screens the top actions live in the AppBar once the card is out of the way: either
  // collapsed (a card that starts collapsed renders nothing there) or scrolled under the AppBar.
  const actionsInAppBar = stickyOnScroll && isSmallScreen && (!expanded || isScrolledPast);

  // Publish collapsed card height as CSS variable so GratiTabBar can offset correctly.
  // Measures only the collapsed header (activeSummary + expand affordance), which is static and
  // never animates — avoids the race between the 260ms timer and the 250ms Collapse animation.
  useEffect(() => {
    if (!stickyOnScroll) return;
    if (!expanded) {
      const el = collapsedHeaderRef.current;
      const update = () => {
        const height = collapsedHeaderRef.current?.offsetHeight ?? 0;
        document.documentElement.style.setProperty('--grati-sticky-card-height', `${height}px`);
      };
      update();
      if (!el) return;
      const ro = new ResizeObserver(update);
      ro.observe(el);
      return () => ro.disconnect();
    } else {
      // Expanding: clear immediately so tab bar follows card upward
      document.documentElement.style.setProperty('--grati-sticky-card-height', '0px');
    }
  }, [stickyOnScroll, expanded, isSmallScreen]);

  // Clean up CSS variable on unmount
  useEffect(() => {
    return () => {
      if (stickyOnScroll) {
        document.documentElement.style.removeProperty('--grati-sticky-card-height');
        document.documentElement.style.overflowAnchor = '';
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When the card is out of the way on small screens, push topActions into the AppBar.
  // Single action → icon button. Multiple actions → Edit icon + MoreVert for the rest.
  // Use a ref so the effect doesn't re-run when callers pass inline arrays.
  const topActionsRef = useRef(topActions);
  topActionsRef.current = topActions;
  useEffect(() => {
    if (!stickyOnScroll) return;
    if (actionsInAppBar) {
      const arr = topActionsRef.current
        ? (Array.isArray(topActionsRef.current) ? topActionsRef.current : [topActionsRef.current])
        : [];
      if (arr.length === 0) {
        updateActions([]);
      } else if (arr.length === 1) {
        updateActions([{ name: arr[0].text, iconElement: arr[0].icon, handleClick: arr[0].actionHandler, variant: 'icon' as const }]);
      } else {
        // Edit action gets its own icon button; everything else goes in MoreVert
        const editAction = arr.find(a => a.text === 'Edit');
        const others = arr.filter(a => a.text !== 'Edit');
        const appBarActions = [];
        if (editAction) {
          appBarActions.push({ name: editAction.text, iconElement: editAction.icon, handleClick: editAction.actionHandler, variant: 'icon' as const });
        }
        if (others.length > 0) {
          appBarActions.push({ name: 'More', iconElement: <MoreVertIcon />, handleClick: () => setAppBarMenuOpen(true), variant: 'icon' as const });
        }
        updateActions(appBarActions);
      }
    } else {
      updateActions([]);
      setAppBarMenuOpen(false);
    }
    return () => { updateActions([]); };
  }, [stickyOnScroll, actionsInAppBar, updateActions]);

  useEffect(() => {
    if (avatar && storageRef) {
      getDownloadURL(firebaseStorageRef(storageRef, avatar))
        .then((url) => {
          setLogo(url);
        })
        .catch((error: { code?: string }) => {
          if (error?.code !== 'storage/object-not-found') {
            console.log(error);
          }
        });
    }
  }, [avatar, storageRef]);

  const handleToggleMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    setMenuOpen((prev) => !prev);
  };

  const handleCloseMenu = () => {
    setMenuOpen(false);
  };

  const handleMenuItemClick = (event: React.MouseEvent, action: GratiCardAction) => {
    event.stopPropagation();
    event.preventDefault();
    handleCloseMenu();
    action.actionHandler();
  };

  const handleToggleExpanded = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (stickyOnScroll) {
      // Expand immediately and scroll to top. Suppress scroll-based collapse during the
      // animation so scroll events on the way to scrollY=0 don't re-collapse the card.
      setExpanded(true);
      onExpandedChange?.(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      const next = !expanded;
      setExpanded(next);
      onExpandedChange?.(next);
    }
  };

  const isCollapsible = collapsible || stickyOnScroll;
  const hasCollapsibleContent = !!(description?.trim() || (Array.isArray(details) ? details.length > 0 : !!details) || placeId || actionbar);

  const TopAction = topActions ? (
    <GratiCardTopAction ownerState={ownerState}>
      {Array.isArray(topActions) && topActions.length > 1 ? (
        <>
          <IconButton
            ref={anchorRef}
            id="card-actions-button"
            aria-controls={menuOpen ? 'card-actions-menu' : undefined}
            aria-expanded={menuOpen ? 'true' : undefined}
            aria-haspopup="true"
            onClick={handleToggleMenu}
            size="small"
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            id="card-actions-menu"
            anchorEl={anchorRef.current}
            open={menuOpen}
            onClose={handleCloseMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            {topActions.map((action, index) => (
              <MenuItem key={index} onClick={(e) => handleMenuItemClick(e, action)}>
                {action.text}
              </MenuItem>
            ))}
          </Menu>
        </>
      ) : (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            const action = Array.isArray(topActions) ? topActions[0] : topActions;
            action?.actionHandler();
          }}
          aria-label={Array.isArray(topActions) ? topActions[0]?.text : topActions.text}
          size="small"
        >
          {Array.isArray(topActions)
            ? topActions[0]?.icon || <MoreVertIcon />
            : topActions.icon || <MoreVertIcon />
          }
        </IconButton>
      )}
    </GratiCardTopAction>
  ) : null;

  // The non-collapsible header region (colorbar + header + body)
  const cardHeaderContent = (
    <>
      <GratiCardColorbar
        ownerState={ownerState}
        onClick={slotClickHandlers?.colorbar ? (e) => { e.stopPropagation(); slotClickHandlers.colorbar?.(); } : undefined}
        sx={{
          ...(colorbar ? { backgroundColor: colorbar } : {}),
          ...(slotClickHandlers?.colorbar ? { cursor: 'pointer', '&:hover': { opacity: 0.8 } } : {}),
        }}
      />
      <GratiCardHeader ownerState={ownerState}>
        <GratiCardHeaderContent ownerState={ownerState}>
          {logo && (
            <GratiCardAvatar
              alt="brand logo"
              id={'eventLogo'}
              src={logo}
              ownerState={ownerState}
              onClick={slotClickHandlers?.avatar ? (e) => { e.stopPropagation(); slotClickHandlers.avatar?.(); } : undefined}
              sx={slotClickHandlers?.avatar ? { cursor: 'pointer', '&:hover': { opacity: 0.8 } } : undefined}
            />
          )}
          {(category || state || (titleInHeader && title)) &&
            <GratiCardContext ownerState={ownerState}>
              <Stack direction='column' spacing={1.5}>
                {category && (
                  <GratiCardCategory
                    ownerState={ownerState}
                    onClick={slotClickHandlers?.category ? (e) => { e.stopPropagation(); slotClickHandlers.category?.(); } : undefined}
                    sx={slotClickHandlers?.category ? interactiveSlotSx : undefined}
                  >
                    {category}
                  </GratiCardCategory>
                )}
                {titleInHeader && title && (
                  <GratiCardTitle
                    ownerState={ownerState}
                    onClick={slotClickHandlers?.title ? (e) => { e.stopPropagation(); slotClickHandlers.title?.(); } : undefined}
                    sx={slotClickHandlers?.title ? { ...interactiveSlotSx, display: 'inline-block' } : undefined}
                  >
                    {title}
                  </GratiCardTitle>
                )}
                {titleInHeader && subtitle && (
                  <GratiCardSubtitle
                    ownerState={ownerState}
                    onClick={slotClickHandlers?.subtitle ? (e) => { e.stopPropagation(); slotClickHandlers.subtitle?.(); } : undefined}
                    sx={{
                      color: 'text.secondary',
                      display: { xs: 'block', sm: 'inline' },
                      ...(slotClickHandlers?.subtitle ? interactiveSlotSx : {}),
                    }}
                  >
                    {subtitle}
                  </GratiCardSubtitle>
                )}
                {state && (
                  <GratiCardState
                    ownerState={ownerState}
                    onClick={slotClickHandlers?.state ? (e) => { e.stopPropagation(); slotClickHandlers.state?.(); } : undefined}
                    sx={slotClickHandlers?.state ? interactiveSlotSx : undefined}
                  >
                    {typeof state === 'string' ? (
                      state
                    ) : (
                      <Button
                        variant={state.variant || 'text'}
                        size="small"
                        startIcon={state.icon}
                        onClick={(e) => {
                          e.stopPropagation();
                          slotClickHandlers?.state ? slotClickHandlers.state() : state.actionHandler();
                        }}
                      >
                        {state.text}
                      </Button>
                    )}
                  </GratiCardState>
                )}
              </Stack>
            </GratiCardContext>
          }
          <Box sx={{ flexGrow: 1 }} />
          {headerActions}
          {spotlight && (() => {
            const spotlightBox = (
              <GratiCardSpotlight
                ownerState={ownerState}
                onClick={slotClickHandlers?.spotlight ? (e) => { e.stopPropagation(); slotClickHandlers.spotlight?.(); } : undefined}
                sx={slotClickHandlers?.spotlight ? interactiveSlotSx : undefined}
              >
                {spotlight}
              </GratiCardSpotlight>
            );
            return spotlightCaption ? (
              <Stack direction="column" sx={{ alignItems: 'center', flexShrink: 0 }}>
                {spotlightBox}
                <Typography variant="labelSmall" noWrap sx={{ color: 'text.secondary' }}>
                  {spotlightCaption}
                </Typography>
              </Stack>
            ) : spotlightBox;
          })()}
          {TopAction}
        </GratiCardHeaderContent>
      </GratiCardHeader>
      {alert && <GratiCardAlert ownerState={ownerState}>{alert}</GratiCardAlert>}
      {!titleInHeader && (
        <GratiCardBody ownerState={ownerState}>
          <Stack sx={{ pl: 1 }}>
            {title && (
              <GratiCardTitle
                ownerState={ownerState}
                onClick={slotClickHandlers?.title ? (e) => { e.stopPropagation(); slotClickHandlers.title?.(); } : undefined}
                sx={slotClickHandlers?.title ? { ...interactiveSlotSx, display: 'inline-block' } : undefined}
              >
                {title}
              </GratiCardTitle>
            )}
            {subtitle && (
              <GratiCardSubtitle
                ownerState={ownerState}
                onClick={slotClickHandlers?.subtitle ? (e) => { e.stopPropagation(); slotClickHandlers.subtitle?.(); } : undefined}
                sx={{
                  color: 'text.secondary',
                  display: { xs: 'block', sm: 'inline' },
                  ...(slotClickHandlers?.subtitle ? interactiveSlotSx : {}),
                }}
              >
                {subtitle}
              </GratiCardSubtitle>
            )}
          </Stack>
        </GratiCardBody>
      )}
    </>
  );

  const collapsibleContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      {description?.trim() && (
        <GratiCardBody ownerState={ownerState}>
          <Stack sx={{ pl: 1 }}>
            <GratiCardDescription
              ownerState={ownerState}
              onClick={slotClickHandlers?.description ? (e) => { e.stopPropagation(); slotClickHandlers.description?.(); } : undefined}
              sx={slotClickHandlers?.description ? interactiveSlotSx : undefined}
            >
              {description}
            </GratiCardDescription>
          </Stack>
        </GratiCardBody>
      )}
      {placeId && (
        <GratiCardDetails ownerState={ownerState}>
          <Stack sx={{ pl: 1 }}>
            <GratiMap placeId={placeId} sx={{ height: 200, width: '100%' }} />
          </Stack>
        </GratiCardDetails>
      )}
      {details && (
        <GratiCardDetails ownerState={ownerState}>
          <Stack sx={{ pl: 1 }}>
            {details && (
              <>
                {Array.isArray(details) ? (
                  details.map((detail, index) => (
                    <GratiCardDetailSlot
                      key={index}
                      ownerState={ownerState}
                      onClick={slotClickHandlers?.detail ? (e) => { e.stopPropagation(); slotClickHandlers.detail?.(index); } : undefined}
                      sx={{
                        ...(slotClickHandlers?.detail ? interactiveSlotSx : {}),
                        ...(detail.highlight ? { backgroundColor: 'warning.light', borderRadius: 1, mx: -0.5, px: 0.5, py: 0.25 } : {}),
                      }}
                    >
                      <Stack direction="row" spacing={1} sx={{
                        alignItems: "center"
                      }}>
                        {detail.icon}
                        <Typography variant="body2">
                          {detail.preText && (
                            <Typography component="span" variant="body2" className="preText">
                              {detail.preText}{' '}
                            </Typography>
                          )}
                          {detail.text}
                          {detail.postText && (
                            <Typography component="span" variant="body2" className="postText">
                              {detail.postText}
                            </Typography>
                          )}
                        </Typography>
                        {detail.status && (
                          <Typography variant="body2" className="status">
                            {detail.status}
                          </Typography>
                        )}
                        {detail.badge}
                        {detail.actionIcon && detail.actionHandler && (
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); detail.actionHandler?.(); }} sx={{ p: 0.25 }}>
                            {detail.actionIcon}
                          </IconButton>
                        )}
                        {!detail.actionIcon && detail.action && detail.actionHandler && (
                          <Typography
                            variant="body2"
                            onClick={detail.actionHandler}
                            sx={{ cursor: 'pointer', textDecoration: 'underline', ml: 1 }}
                          >
                            {detail.action}
                          </Typography>
                        )}
                      </Stack>
                    </GratiCardDetailSlot>
                  ))
                ) : (
                  <GratiCardDetailSlot
                    ownerState={ownerState}
                    onClick={slotClickHandlers?.detail ? (e) => { e.stopPropagation(); slotClickHandlers.detail?.(0); } : undefined}
                    sx={{
                      ...(slotClickHandlers?.detail ? interactiveSlotSx : {}),
                      ...(details.highlight ? { backgroundColor: 'warning.light', borderRadius: 1, px: 0.5 } : {}),
                    }}
                  >
                    <Stack direction="row" spacing={1} sx={{
                      alignItems: "center"
                    }}>
                      {details.icon}
                      <Typography variant="body2">
                        {details.preText && (
                          <Typography component="span" variant="body2" className="preText">
                            {details.preText}{' '}
                          </Typography>
                        )}
                        {details.text}
                        {details.postText && (
                          <Typography component="span" variant="body2" className="postText">
                            {details.postText}
                          </Typography>
                        )}
                      </Typography>
                      {details.status && (
                        <Typography variant="body2" className="status">
                          {details.status}
                        </Typography>
                      )}
                      {details.badge}
                      {details.actionIcon && details.actionHandler && (
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); details.actionHandler?.(); }} sx={{ p: 0.25 }}>
                          {details.actionIcon}
                        </IconButton>
                      )}
                      {!details.actionIcon && details.action && details.actionHandler && (
                        <Typography
                          variant="body2"
                          onClick={details.actionHandler}
                          sx={{ cursor: 'pointer', textDecoration: 'underline', ml: 1 }}
                        >
                          {details.action}
                        </Typography>
                      )}
                    </Stack>
                  </GratiCardDetailSlot>
                )}
              </>
            )}
          </Stack>
        </GratiCardDetails>
      )}
      <CardActions sx={{ mt: 'auto' }}>
        {actionbar && (
          <GratiCardActionbar ownerState={ownerState}>
            {actionbar}
          </GratiCardActionbar>
        )}
      </CardActions>
    </Box>
  );

  // Default collapsed content. On small screens with stickyOnScroll, the title is in the AppBar,
  // so show only the colorbar accent. On large screens (or non-sticky), show the title.
  const defaultCollapsedSummary = (stickyOnScroll && isSmallScreen) ? (
    <GratiCardCollapsedName
      ownerState={ownerState}
      sx={colorbar ? { borderLeft: `3px solid ${colorbar}` } : undefined}
    />
  ) : (
    <GratiCardCollapsedName
      ownerState={ownerState}
      sx={colorbar ? { borderLeft: `3px solid ${colorbar}` } : undefined}
    >
      <Typography
        variant="titleMedium"
        sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
      >
        {title ?? ''}
      </Typography>
    </GratiCardCollapsedName>
  );

  const activeSummary = collapsedSummary ?? defaultCollapsedSummary;

  // Expand affordance shown below the name row when collapsed.
  // Suppressed for stickyOnScroll on small screens — scrolling back to top re-expands instead.
  // On large screens with stickyOnScroll the affordance is still shown.
  const expandAffordance = isCollapsible && !expanded && hasCollapsibleContent && expandLabel && !(stickyOnScroll && isSmallScreen) ? (
    <GratiCardExpandAffordance
      ownerState={ownerState}
      role="button"
      tabIndex={0}
      onClick={handleToggleExpanded}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleToggleExpanded(e as unknown as React.MouseEvent); }}
    >
      <ExpandMoreIcon fontSize="small" />
      <Typography variant="caption">{expandLabel}</Typography>
    </GratiCardExpandAffordance>
  ) : null;

  return (
    <GratiCardRoot
      ref={(node) => {
        internalRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) (ref as React.RefObject<HTMLDivElement | null>).current = node;
      }}
      ownerState={ownerState}
      sx={isStickyCollapse ? {
        position: 'sticky',
        top: APPBAR_STICKY_TOP,
        zIndex: (theme) => isSmallScreen ? theme.zIndex.appBar - 2 : theme.zIndex.appBar - 1,
        // Always set maxWidth/borderRadius so CSS can interpolate between the two values
        maxWidth: expanded ? '37.25rem' : '100%',
        borderRadius: expanded ? '1rem' : 0,
        boxShadow: expanded ? 'none' : (theme) => theme.shadows[4],
        // MD3 "emphasized" easing for spatial transitions
        transition: 'max-width 300ms cubic-bezier(0.2, 0, 0, 1), border-radius 300ms cubic-bezier(0.2, 0, 0, 1), box-shadow 300ms cubic-bezier(0.2, 0, 0, 1)',
      } : undefined}
      {...other}
    >
      {isCollapsible && !expanded ? (
        (stickyOnScroll && isSmallScreen) ? null : (
          <Box ref={collapsedHeaderRef}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {to ? (
                <CardActionArea
                  component={Link}
                  to={to}
                  onClick={onNavigate ? (e: React.MouseEvent) => {
                    if (e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey) {
                      e.preventDefault();
                      onNavigate();
                    }
                  } : undefined}
                  sx={{ flex: 1 }}
                >
                  {activeSummary}
                </CardActionArea>
              ) : (
                <Box
                  role="button"
                  tabIndex={0}
                  onClick={handleToggleExpanded}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleToggleExpanded(e as unknown as React.MouseEvent); }}
                  sx={{ cursor: 'pointer', flex: 1 }}
                >
                  {activeSummary}
                </Box>
              )}
              {TopAction}
            </Box>
            {expandAffordance}
          </Box>
        )
      ) : (
        to ? (
          <CardActionArea
            component={Link}
            to={to}
            onClick={onNavigate ? (e: React.MouseEvent) => {
              if (e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey) {
                e.preventDefault();
                onNavigate();
              }
            } : undefined}
          >
            {cardHeaderContent}
          </CardActionArea>
        ) : (
          cardHeaderContent
        )
      )}
      {isCollapsible ? (
        <Collapse in={expanded} timeout={250}>
          {collapsibleContent}
        </Collapse>
      ) : (
        collapsibleContent
      )}
      {/* MoreVert menu for AppBar when the actions are up there and there are several */}
      {actionsInAppBar && (() => {
        const arr = Array.isArray(topActions) ? topActions : (topActions ? [topActions] : []);
        const menuActions = arr.filter(a => a.text !== 'Edit');
        if (menuActions.length === 0) return null;
        return (
          <Menu
            open={appBarMenuOpen}
            onClose={() => setAppBarMenuOpen(false)}
            anchorReference="anchorPosition"
            anchorPosition={{ top: 60, left: typeof window !== 'undefined' ? window.innerWidth - 8 : 400 }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {menuActions.map((action, i) => (
              <MenuItem key={i} onClick={() => { action.actionHandler(); setAppBarMenuOpen(false); }}>
                {action.text}
              </MenuItem>
            ))}
          </Menu>
        );
      })()}
    </GratiCardRoot>
  );
});
